import { faTrashCan } from '@fortawesome/free-regular-svg-icons';
import { faSearch } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, useEffect, useState } from 'react';
import { Axios, getErrorMessage } from '@helpers/ApiService';
import {
  errorMessage,
  fieldAttributes,
  onboardingStepId,
  pagination,
  responseTexts,
  speciesSubsteps,
} from '@helpers/Constants';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  SpeciesService,
  ApiServiceResponse,
} from '@helpers/Models';
import { useToast } from '@contexts/Toast';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Assets } from '@helpers/Constants/Assets';
import { Breadcrumbs } from '@components/Breadcrumbs';
import { ButcherHeader } from '@components/ButcherHeader';
import Pagination from '@components/Pagination';
import { UseConfirmationToast } from '@contexts/ConfirmationToast';
import { isModified } from '@services/isModified';
import { OnBoardSettings } from '@components/OnBoardSettings';
import { useLocalStorage } from '@hooks/useLocalStorage';
import { useUnsavedChangesPrompt } from '@hooks/useNavigationBlocker';
import { DuplicateValidation } from '@helpers/DuplicateValidation';

// Extend SpeciesService to include deletedAt for full list
interface FullService extends SpeciesService {
  deletedAt: string | null;
}

export const Services: FC = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();
  const { showConfirmationToast } = UseConfirmationToast();
  const { updateOnboardingStep } = useLocalStorage();
  const location = useLocation();
  const fromSettings = location.state?.fromSettings;

  // Active services (deletedAt === null)
  const [services, setServices] = useState<SpeciesService[]>([]);
  // Full list from API (includes soft‑deleted services)
  const [allServices, setAllServices] = useState<FullService[]>([]);
  const [initialList, setInitialList] = useState<SpeciesService[]>([]);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [allSelected, setAllSelected] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(pagination.itemsPerPage);
  const [totalPages, setTotalPages] = useState(1);
  const [searchKey, setSearchKey] = useState('');
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // ---- Derive unsaved changes from comparison ----
  useEffect(() => {
    setHasUnsavedChanges(isModified(initialList, services));
  }, [services, initialList]);

  useUnsavedChangesPrompt(hasUnsavedChanges, undefined, 'Leave Without Saving');

  // ---- Fetch services (full list) ----
  const fetchService = async (
    page: number,
    searchKey: string,
    count: number = itemsPerPage,
  ): Promise<void> => {
    try {
      const response = await Axios.post(ApiEndpoints.FETCH_SERVICE, {
        page,
        count,
        searchKey,
      });
      // Full list (including deleted)
      const fullServices: FullService[] = response.data.data.services.map(
        (service: any) => ({
          id: service.id,
          name: service.name,
          description: service.description,
          availableFlag: Boolean(service.availableFlag),
          colorFlag: Boolean(service.colorFlag),
          deletedAt: service.deletedAt || null,
        })
      );
      setAllServices(fullServices);

      // Active services (deletedAt === null)
      const activeServices = fullServices.filter(
        (service: FullService) => service.deletedAt === null
      );
      const normalizedServices = activeServices.map(({ deletedAt, ...rest }) => rest);
      setServices(normalizedServices);
      setInitialList(normalizedServices);
      setCurrentPage(response.data.data.currentPage || 1);
      setTotalPages(response.data.data.totalPages || 1);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  useEffect(() => {
    if (searchKey !== '') {
      setCurrentPage(1);
    }
    fetchService(currentPage, searchKey, itemsPerPage);
  }, [currentPage, itemsPerPage, searchKey]);

  // ---- Find a soft‑deleted service by name (case‑insensitive) ----
  const findDeletedServiceByName = (name: string): FullService | undefined => {
    return allServices.find(
      (s: FullService) =>
        s.deletedAt !== null &&
        s.name.toLowerCase() === name.toLowerCase().trim()
    );
  };

  // ---- Validation ----
  const validateField = (
    name: string,
    value: string,
    index?: number,
  ): string => {
    switch (name) {
      case 'name': {
        if (!value.trim()) {
          return errorMessage.FIELD_REQUIRED;
        }
        const isDuplicate = services.some((service, i) => {
          if (index !== undefined && i === index) return false;
          return service.name.toLowerCase() === value.toLowerCase().trim();
        });
        return isDuplicate ? errorMessage.DUPLICATE_SERVICE : '';
      }
      case 'description': {
        if (value.length > fieldAttributes.DESCRIPTION_LIMIT) {
          return errorMessage.DESCRIPTION_EXEEDS_150_CHARECTER;
        }
        return '';
      }
      default:
        return '';
    }
  };

  const validateAll = async (): Promise<boolean> => {
    const newErrors: { [key: string]: string } = {};
    services.forEach((service, idx) => {
      const nameError = validateField('name', service.name, idx);
      if (nameError) newErrors[`name-${idx}`] = nameError;
      const descError = validateField('description', service.description, idx);
      if (descError) newErrors[`description-${idx}`] = descError;
    });
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return true;
    }

    // Duplicate check across pages (if more than one page)
    if (totalPages > 1) {
      for (const service of services) {
        const isDuplicate = await DuplicateValidation(
          service,
          ApiEndpoints.FETCH_SERVICE,
          'service',
        );
        if (isDuplicate) {
          const idx = services.findIndex(
            (s) => s.name === service.name && s.description === service.description,
          );
          if (idx !== -1) {
            newErrors[`name-${idx}`] = errorMessage.DUPLICATE_ERROR;
          } else {
            newErrors['form'] = errorMessage.DUPLICATE_ERROR;
          }
        }
      }
      setErrors(newErrors);
      if (Object.keys(newErrors).length > 0) {
        return true;
      }
    }

    return false;
  };

  // ---- Handlers for inline editing ----
  const handleServiceFieldChange = (
    index: number,
    field: 'name' | 'description',
    value: string,
  ): void => {
    const updatedServices = [...services];
    updatedServices[index] = {
      ...updatedServices[index],
      [field]: value,
    };
    setServices(updatedServices);

    const error = validateField(field, value, index);
    const errorKey = `${field}-${index}`;
    setErrors((prev) => ({
      ...prev,
      [errorKey]: error,
    }));
  };

  const handleServiceFlagChange = (
    index: number,
    flag: 'colorFlag' | 'availableFlag',
    checked: boolean,
  ): void => {
    const updatedServices = [...services];
    updatedServices[index] = {
      ...updatedServices[index],
      [flag]: checked,
    };
    setServices(updatedServices);
  };

  const handleSelectAll = (): void => {
    const dataSelected = !allSelected;
    setAllSelected(dataSelected);
    const updatedServices = services.map((service) => ({
      ...service,
      availableFlag: dataSelected,
    }));
    setServices(updatedServices);
  };

  // ---- Add new empty service (with validation) ----
  const handleAddMore = (): void => {
    // Check if there is any service with empty name (new row)
    const hasEmptyName = services.some((s) => !s.name || !s.name.trim());
    if (hasEmptyName) {
      showToastMessage('Please complete the service entry before creating a new one.', 'error');
      return;
    }

    const newService: SpeciesService = {
      name: '',
      description: '',
      colorFlag: false,
      availableFlag: true,
    };
    setServices([...services, newService]);
    setTimeout(() => {
      const scrollArea = document.getElementById('service-table-scroll-area');
      if (scrollArea) {
        scrollArea.scrollTop = scrollArea.scrollHeight;
      }
    }, 100);
  };

  // ---- Delete (handles both saved and unsaved) ----
  const performDelete = async (
    id: number | undefined,
    index: number,
  ): Promise<void> => {
    try {
      if (id !== undefined) {
        // Saved record – delete via API
        await Axios.delete(ApiEndpoints.DELETE_SERVICE, {
          data: { ids: [id] },
        });
        // Refetch the full list to update allServices and services
        await fetchService(currentPage, searchKey, itemsPerPage);
        showToastMessage('Service deleted successfully', 'success');
      } else {
        // Unsaved row – just remove from list
        const newServices = services.filter((_, i) => i !== index);
        setServices(newServices);
        showToastMessage('Service removed', 'success');
      }
    } catch (error) {
      const statusCode =
        (error as {
          response?: {
            status?: number;
            data?: unknown;
          };
        })?.response?.status;

      const extractApiMessage = (payload: unknown): string | undefined => {
        if (!payload) return undefined;

        if (Array.isArray(payload)) {
          for (const item of payload) {
            const nested = extractApiMessage(item);
            if (nested) return nested;
          }
          return undefined;
        }

        if (typeof payload === 'object' && payload !== null) {
          const candidate = payload as {
            message?: unknown;
            errors?: unknown;
            data?: unknown;
          };

          if (typeof candidate.message === 'string' && candidate.message.trim()) {
            return candidate.message;
          }

          const fromErrors = extractApiMessage(candidate.errors);
          if (fromErrors) return fromErrors;

          const fromData = extractApiMessage(candidate.data);
          if (fromData) return fromData;

          for (const value of Object.values(candidate)) {
            const nested = extractApiMessage(value);
            if (nested) return nested;
          }
        }

        return undefined;
      };

      if (statusCode === 400) {
        const responseData = (error as {
          response?: {
            data?: unknown;
          };
        })?.response?.data;

        const apiMessage = extractApiMessage(responseData);
        if (apiMessage) {
          showToastMessage(apiMessage, 'error');
          return;
        }
      }

      showToastMessage(errorMessage.DELETE_FAILED, 'error');
    }
  };

  const handleDelete = (id: number | undefined, index: number): void => {
    showConfirmationToast(
      'Are you sure you want to delete this item?',
      'Delete Confirmation',
      'delete',
      () => performDelete(id, index),
      'Delete',
      undefined,
      'Cancel',
    );
  };

  // ---- Save ----
  const handleSave = async (): Promise<void> => {
    const hasErrors = await validateAll();
    if (hasErrors) {
      showToastMessage('Please fix all errors before saving.', 'error');
      return;
    }

    if (services.length === 0) {
      showToastMessage(errorMessage.REQUIRED_ERROR, 'error');
      return;
    }

    const isDataModified = isModified(initialList, services);
    if (initialList && !isDataModified) {
      showToastMessage('No changes to save.', 'success');
      return;
    }

    // ---- Check for new services that match soft‑deleted ones ----
    const restoreIds: number[] = [];
    const servicesToKeep: SpeciesService[] = [];
    const servicesToRestoreNames: string[] = [];

    for (const service of services) {
      if (service.id === undefined) {
        // This is a new row – check if it exists in deleted state
        const deleted = findDeletedServiceByName(service.name);
        if (deleted) {
          restoreIds.push(deleted.id!);
          servicesToRestoreNames.push(service.name);
          // Do not include this service in the upsert payload
          continue;
        }
      }
      servicesToKeep.push(service);
    }

    // ---- Restore any matching deleted services ----
    if (restoreIds.length > 0) {
      try {
        await Axios.post(ApiEndpoints.RESTORE_SERVICE, { ids: restoreIds });
        showToastMessage(
          `Service added succesfully`,
          'success'
        );
        // After restore, refresh the list to reflect restored records
        await fetchService(currentPage, searchKey, itemsPerPage);
        return;
      } catch (error) {
        showToastMessage('Failed to restore service(s).', 'error');
        return;
      }
    }

    // ---- Proceed with upsert for the remaining services ----
    const payload = servicesToKeep.map((service) => ({
      ...(service.id && { id: service.id }),
      name: service.name,
      description: service.description,
      availableFlag: service.availableFlag ? 1 : 0,
      colorFlag: service.colorFlag ? 1 : 0,
    }));

    try {
      const response = await Axios.post(ApiEndpoints.UPSERT_SERVICE, payload);
      if (response.data && response.data.data) {
        const updatedServices = response.data.data.map(
          (item: ApiServiceResponse) => ({
            id: item.id,
            name: item.name,
            description: item.description,
            availableFlag: Boolean(item.availableFlag),
            colorFlag: Boolean(item.colorFlag),
          })
        );
        setServices(updatedServices);
        setInitialList(updatedServices);

        // Build success message
        const newItemsCount = servicesToKeep.filter((item) => !item.id).length;
        let updatedCount = 0;
        const updatedMap = new Map(
          updatedServices.map((item: SpeciesService) => [item.id, item])
        );
        for (const item of initialList) {
          if (item.id && updatedMap.has(item.id)) {
            const updatedItem = updatedMap.get(item.id);
            if (updatedItem) {
              const initialCopy = JSON.parse(JSON.stringify(item));
              const updatedCopy = JSON.parse(JSON.stringify(updatedItem));
              delete initialCopy.id;
              delete updatedCopy.id;
              if (JSON.stringify(initialCopy) !== JSON.stringify(updatedCopy)) {
                updatedCount++;
              }
            }
          }
        }
        const operations: string[] = [];
        if (newItemsCount > 0) {
          operations.push(
            `${newItemsCount} Service${newItemsCount > 1 ? 's' : ''} added`
          );
        }
        if (updatedCount > 0) {
          operations.push(
            `${updatedCount} Service${updatedCount > 1 ? 's' : ''} updated`
          );
        }
        let message = 'No changes made to services';
        if (operations.length === 1) {
          message = `${operations[0]} successfully`;
        } else if (operations.length === 2) {
          message = `${operations[0]} and ${operations[1]} successfully`;
        } else if (operations.length === 3) {
          message = `${operations[0]}, ${operations[1]}, and ${operations[2]} successfully`;
        }
        if (message === 'No changes made to services' && (newItemsCount > 0 || updatedCount > 0)) {
          message = 'Services updated successfully';
        }
        showToastMessage(message, 'success');
      } else {
        const hadExisting = initialList.some((s) => s.id !== undefined);
        showToastMessage(
          hadExisting
            ? 'Services updated successfully'
            : responseTexts.SERVICE_CREATE_SUCCESS,
          'success'
        );
      }
    } catch (error) {
      showToastMessage(getErrorMessage(error), 'error');
      throw error;
    }
  };

  // ---- Navigation ----
  const navigateToNextPage = () => {
    const hasColorEnabled = services.some((service) => service.colorFlag);
    updateOnboardingStep(onboardingStepId.SERVICES);
    if (hasColorEnabled) {
      if (fromSettings) {
        navigate(FrontendRoutes.COLOR, { state: { fromSettings } });
      } else {
        navigate(FrontendRoutes.COLOR);
      }
    } else {
      updateOnboardingStep(onboardingStepId.COLOR);
      navigate(FrontendRoutes.SPECIES_AND_SERVICE, {
        state: { colorStepSkipped: true, fromSettings },
      });
    }
  };

  const handleNext = async () => {
    const hasErrors = await validateAll();
    if (hasErrors) {
      showToastMessage('Please fix all errors before proceeding.', 'error');
      return;
    }

    const hasSavedService = services.some((s) => s.id !== undefined);
    if (!hasSavedService) {
      showToastMessage('Please save at least one service before proceeding.', 'error');
      return;
    }

    if (hasUnsavedChanges) {
      showConfirmationToast(
        'You have unsaved changes. Please save before navigating to another tab.',
        'Unsaved Changes',
        'info',
        () => {
          navigateToNextPage();
        },
        'Leave without saving',
        () => {
          // Stay on current page
        },
        'Stay on this tab'
      );
      return;
    }

    navigateToNextPage();
  };

  // ---- Search ----
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchKey(e.target.value);
  };

  const hasErrors = (): boolean => {
    return Object.values(errors).some((err) => err !== '');
  };

  // ---- Render ----
  const renderContent = (
    <>
      <div>
        <Breadcrumbs
          title="Configure Settings"
          activeMainNav="Species"
          activeStep={2}
          subNavSteps={speciesSubsteps}
          fromSettings={fromSettings ? true : false}
          forStoreSetting={false}
        />
      </div>
      <div className="mt-3 rounded-lg">
        <div className="flex justify-between items-center mb-6">
          <h1 className="font-semibold text-xl px-5 mt-3">Services</h1>
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                onChange={handleSearchChange}
                value={searchKey}
                className="border p-2 pl-3 pr-10 w-80 accent-primary focus:outline-primary border-gray-300 rounded"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              />
            </div>
          </div>
        </div>
        <div
          className="flex-1 overflow-y-auto mb-6 relative shadow-lg h-[calc(100vh-480px)]"
          id="service-table-scroll-area"
        >
          <table className="w-full table-auto">
            <thead className="bg-[#F0F0F0] sticky top-0 z-10">
              <tr className="border-b">
                <th className="px-4 py-2 w-[40%] text-left">
                  <div className="flex items-center gap-1 text-[16px] font-semibold text-[#1C1B1F]">
                    <span>Service Type & Description</span>
                    <img src={Assets.SORT_ICON} alt="Sort" className="h-3 w-3" />
                  </div>
                </th>
                <th className="px-4 py-2 w-[20%] text-center">
                  <p className="text-[16px] font-semibold text-[#1C1B1F]">Color</p>
                </th>
                <th className="px-4 py-2 w-[20%] text-center">
                  <p className="text-[16px] whitespace-nowrap font-semibold text-[#1C1B1F]">
                    Available{' '}
                    <input
                      onChange={handleSelectAll}
                      type="checkbox"
                      className="ml-2 form-checkbox h-4 w-4 accent-primary focus:ring-primary text-primary rounded-full"
                    />
                  </p>
                </th>
                <th className="px-4 py-2 w-[20%] text-center">
                  <p className="text-[16px] font-semibold text-[#1C1B1F]"></p>
                </th>
              </tr>
            </thead>
            <tbody>
              {services.length > 0 ? (
                services.map((service, index) => (
                  <tr key={service.id ?? `new-${index}`}>
                    <td className="px-4 py-2 w-[60%] align-top">
                      <div className="relative">
                        <input
                          type="text"
                          name="name"
                          placeholder="Service name"
                          value={service.name}
                          onChange={(e) =>
                            handleServiceFieldChange(index, 'name', e.target.value)
                          }
                          className={`text-[14px] border-b-2 border-[#555F71] font-medium pb-0.2 focus:outline-none focus:border-primary ${errors[`name-${index}`] ? 'border-red-500' : ''
                            }`}
                        />
                        {errors[`name-${index}`] && (
                          <p className="text-red-500 text-[11px] mt-1">
                            {errors[`name-${index}`]}
                          </p>
                        )}
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          name="description"
                          placeholder="Add description here..."
                          value={service.description}
                          onChange={(e) =>
                            handleServiceFieldChange(index, 'description', e.target.value)
                          }
                          className={`w-full text-[13px] text-gray-500 placeholder-gray-400 pt-0 focus:outline-none ${errors[`description-${index}`] ? 'border-red-500' : ''
                            }`}
                        />
                        {errors[`description-${index}`] && (
                          <p className="text-red-500 text-[11px] mt-1">
                            {errors[`description-${index}`]}
                          </p>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2 w-[20%] text-center">
                      <input
                        type="checkbox"
                        name="colorFlag"
                        checked={service.colorFlag}
                        onChange={(e) =>
                          handleServiceFlagChange(index, 'colorFlag', e.target.checked)
                        }
                        className="h-4 w-4 accent-primary focus:ring-primary border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-4 py-2 w-[20%] text-center">
                      <input
                        type="checkbox"
                        name="availableFlag"
                        checked={service.availableFlag}
                        onChange={(e) =>
                          handleServiceFlagChange(index, 'availableFlag', e.target.checked)
                        }
                        className="h-4 w-4 accent-primary focus:ring-primary border-gray-300 rounded"
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-4">
                        <button
                          type="button"
                          onClick={() => handleDelete(service.id, index)}
                        >
                          <FontAwesomeIcon icon={faTrashCan} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center p-4">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {errors.form && (
          <p className="text-red-500 text-[15px]">{errors.form}</p>
        )}
        <div>
          <Pagination
            totalPages={totalPages}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={setCurrentPage}
            onItemsPerPageChange={setItemsPerPage}
          />
        </div>
      </div>
      <div className="sticky bottom-0 bg-white mt-3 border-t border-gray-100">
        <div className="w-full py-6">
          <div className="flex flex-col 2xl:flex-row 2xl:items-center 2xl:justify-end gap-4 w-full">
            <button
              onClick={handleAddMore}
              type="button"
              className="w-full 2xl:w-[150px] h-8 px-6 flex items-center justify-center space-x-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-200"
            >
              <span>+ Add More</span>
            </button>
            <div className="w-full 2xl:w-auto flex items-center justify-between 2xl:justify-end gap-4">
              <button
                type="button"
                onClick={handleSave}
                disabled={!hasUnsavedChanges}
                className={`w-1/2 2xl:w-[130px] h-8 px-6 border rounded-md text-sm font-medium flex items-center justify-center transition-all duration-200 ${hasUnsavedChanges
                  ? 'bg-white text-primary border-primary hover:bg-primary hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                  : 'bg-white text-[#00483480] cursor-not-allowed border-[#00483480] pointer-events-auto'
                  }`}
              >
                Save
              </button>
              <button
                type="button"
                onClick={handleNext}
                disabled={hasUnsavedChanges || hasErrors()}
                className={`w-1/2 2xl:w-[150px] h-8 px-6 border rounded-md text-sm font-medium flex items-center justify-center space-x-2 transition-all duration-200 ${hasUnsavedChanges || hasErrors()
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed border-gray-300'
                  : 'bg-primary text-white hover:bg-dark-primary border-transparent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary'
                  }`}
              >
                <span>Next</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  return (
    <>
      {!fromSettings ? (
        <div className="w-screen h-screen overflow-auto flex justify-center items-start">
          <div className="w-11/12 p-4">
            <ButcherHeader isOnBoarding={true} />
            {renderContent}
          </div>
        </div>
      ) : (
        <OnBoardSettings renderContent={renderContent} speciesSetup={true} />
      )}
    </>
  );
};